import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  Link as LinkIcon,
  Timeline,
  Timer,
  Speed,
  Favorite,
  Bolt,
  Terrain,
  Sync as SyncIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { subDays, format } from 'date-fns';
import apiClient from '../../../utils/apiClient';
import { useAppDispatch } from '../../../store/hooks';
import { baseApi } from '../../../store/api/baseApi';

// Import the RTK Query hook
import { useGetActivitiesQuery } from '../../../store/api/baseApi';

// Import the relevant types for our component
import type { ActivityResponse } from '../../../store/api/models/activityModels';

// Import ActivityFilters component
import ActivityFilters, { type ActivityFilterValues } from '../components/ActivityFilters';
import { PolylineMap } from '../components/PolylineMap';
import { WorkoutLinkButton } from '../components/WorkoutLinkButton';

// Define a type for the view mode
type ViewMode = 'grid' | 'list';

// Helper to format pace from m/s to min/km
const formatPace = (metersPerSecond?: number): string => {
  if (!metersPerSecond || metersPerSecond === 0) return '--:--';
  const secondsPerKm = 1000 / metersPerSecond;
  const minutes = Math.floor(secondsPerKm / 60);
  const seconds = Math.floor(secondsPerKm % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Helper to format speed from m/s to km/h
const formatSpeed = (metersPerSecond?: number): string => {
  if (!metersPerSecond) return '--';
  return ((metersPerSecond * 3600) / 1000).toFixed(1);
};

// --- ActivityMetrics Component ---
const ActivityMetrics = ({ activity }: { activity: ActivityResponse }) => {
  const isRun = activity.sport_type.toLowerCase().includes('run');

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
      {/* Distance */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Distance">
        <Timeline sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {((activity.distance || 0) / 1000).toFixed(2)} km
        </Typography>
      </Box>

      {/* Time */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Duration">
        <Timer sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {Math.floor((activity.moving_time || 0) / 60)} min
        </Typography>
      </Box>

      {/* Pace / Speed */}
      {(activity.average_speed || 0) > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title={isRun ? "Avg Pace" : "Avg Speed"}>
          <Speed sx={{ fontSize: 16, color: 'primary.main' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {isRun
              ? `${formatPace(activity.average_speed)} /km`
              : `${formatSpeed(activity.average_speed)} km/h`
            }
          </Typography>
        </Box>
      )}

      {/* Heart Rate */}
      {activity.average_heartrate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Avg Heart Rate">
          <Favorite sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {activity.average_heartrate.toFixed(0)} bpm
          </Typography>
        </Box>
      )}

      {/* Power */}
      {activity.average_watts && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Avg Power">
          <Bolt sx={{ fontSize: 16, color: 'warning.main' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {activity.average_watts.toFixed(0)} W
          </Typography>
        </Box>
      )}

      {/* Elevation */}
      {activity.total_elevation_gain && activity.total_elevation_gain > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Elevation Gain">
          <Terrain sx={{ fontSize: 16, color: 'success.main' }} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {activity.total_elevation_gain.toFixed(0)} m
          </Typography>
        </Box>
      )}

      {/* TSS */}
      {activity.training_stress_score && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title="Training Stress Score">
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'info.main', border: 1, borderColor: 'info.main', borderRadius: 0.5, px: 0.5, lineHeight: 1.2 }}>
            TSS
          </Typography>
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {(activity.training_stress_score || 0).toFixed(0)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// --- GridView Component (Existing Card Logic) ---
const ActivityGridView = ({ activities, navigate }: { activities: ActivityResponse[], navigate: any }) => (
  <Grid container spacing={3}>
    {activities.map((activity) => (
      <Grid item xs={12} sm={6} md={4} lg={3} key={activity.id}>
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 3,
            },
          }}
          onClick={() => navigate(`/athlete/activities/${activity.id}`)}
        >
          {/* Route Thumbnail */}
          {activity.maps?.summary_polyline && (
            <Box sx={{ p: 2, pb: 0 }}>
              <PolylineMap
                polyline={activity.maps.summary_polyline}
                height={120}
                strokeWidth={2}
                minimal={true}
              />
            </Box>
          )}

          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" noWrap sx={{ flex: 1, pr: 1 }}>
                {activity.name}
              </Typography>
              <WorkoutLinkButton activityId={activity.id} workoutId={activity.workout_id} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip label={activity.sport_type} size="small" color="primary" />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                • {new Date(activity.start_date || '').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
              </Typography>
            </Box>

            <ActivityMetrics activity={activity} />
            {activity.tags && activity.tags.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {activity.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    ))}
  </Grid>
);

// --- ListView Component (New Feed-like Logic) ---
const ActivityListView = ({ activities, navigate }: { activities: ActivityResponse[], navigate: any }) => (
  <Box>
    {activities.map((activity, index) => (
      <Box key={activity.id}>
        <ListItem
          alignItems="flex-start"
          sx={{ cursor: 'pointer', py: 2 }}
          onClick={() => navigate(`/athlete/activities/${activity.id}`)}
          secondaryAction={
            <WorkoutLinkButton activityId={activity.id} workoutId={activity.workout_id} />
          }
        >
          <ListItemAvatar>
            {activity.maps?.summary_polyline ? (
              <Box sx={{ width: 80, height: 80, mr: 1 }}>
                <PolylineMap
                  polyline={activity.maps.summary_polyline}
                  height={80}
                  width={80}
                  strokeWidth={1.5}
                  minimal={true}
                />
              </Box>
            ) : (
              <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                {activity.sport_type.charAt(0)}
              </Avatar>
            )}
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {activity.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Chip label={activity.sport_type} size="small" color="primary" />
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    • {new Date(activity.start_date || '').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              </Box>
            }
            secondary={
              <Box sx={{ mt: 1 }}>
                <ActivityMetrics activity={activity} />
                {activity.tags && activity.tags.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {activity.tags.map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                )}
              </Box>
            }
          />
        </ListItem>
        {index < activities.length - 1 && <Divider variant="inset" />}
      </Box>
    ))}
  </Box>
);


/**
 * ActivitiesListPage Component
 *
 * Displays a paginated and filterable list of activities for the authenticated athlete.
 * Integrates with RTK Query for data fetching, supports offline mode with cached data,
 * and provides filtering capabilities by date range, sport type, and distance.
 *
 * Features:
 * - Paginated activities list with configurable page size
 * - Activity filtering (date range, sport type, distance)
 * - Offline support with cached data indicators
 * - Loading states with skeleton components
 * - Toggle between Grid and List view
 * - URL-based state management for filters and pagination
 */
const ActivitiesListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // State for view mode
  const [viewMode, setViewMode] = useState<ViewMode>('grid');

  // State for pagination
  const [page, setPage] = useState(1);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [isSyncingMore, setIsSyncingMore] = useState(false);

  // Intersection Observer refs
  const observer = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // State for smooth infinite scroll (growing list)
  const [accumulatedActivities, setAccumulatedActivities] = useState<ActivityResponse[]>([]);
  const [hasNoMoreInDb, setHasNoMoreInDb] = useState(false);

  // State for filters
  const [filters, setFilters] = useState<ActivityFilterValues>({
    startDate: null,
    endDate: null,
    sportType: 'all',
    distanceRange: [0, 200],
    searchQuery: '',
    workoutId: 'all',
  });

  // Build API query parameters from filters
  const queryParams = React.useMemo(() => {
    const params: any = {
      page,
      per_page: 30,
    };

    // Add workout filter if selected
    if (filters.workoutId !== 'all') {
      params.workout_id = filters.workoutId as number;
    }

    // Add sport type filter if selected
    if (filters.sportType !== 'all') {
      params.sport_type = filters.sportType;
    }

    // Add date range filters if set
    if (filters.startDate) {
      params.after = filters.startDate.toISOString();
    }
    if (filters.endDate) {
      params.before = filters.endDate.toISOString();
    }

    return params;
  }, [page, filters]);

  // RTK Query hook for the main list
  const {
    data: activitiesData,
    error,
    isLoading,
    isFetching,
  } = useGetActivitiesQuery(queryParams);

  // Synchronize the growing list with the fetched page
  useEffect(() => {
    if (activitiesData?.activities) {
      if (page === 1) {
        // Reset list on first page (e.g. filter change)
        setAccumulatedActivities(activitiesData.activities);
      } else {
        // Append new page items, deduplicating by ID
        setAccumulatedActivities(prev => {
          const newItems = activitiesData.activities.filter(
            n => !prev.some(p => p.id === n.id)
          );
          return [...prev, ...newItems];
        });
      }
    }
  }, [activitiesData, page]);

  // Extract variables for easier access
  const activities = accumulatedActivities;
  const totalPages = activitiesData?.total_pages || 1;

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    console.log(`Navigate to page ${value}`);
  };

  // Logic to fetch more (Gradual Loading & Sync)
  const fetchMoreActivities = useCallback(async () => {
    // Basic guards
    if (isSyncingMore || isFetching || isLoading || isOffline || hasNoMoreInDb) return;

    // Safety check
    if (activities.length === 0) return;

    try {
      setIsSyncingMore(true);

      // 1. Check if we have more local pages of data already in the DB
      const hasMoreLocalPages = activitiesData && page < (activitiesData.total_pages || 1);

      if (hasMoreLocalPages) {
        // We HAVE more filtered data in the DB. Load the next page.
        setPage(prev => prev + 1);
      } else {
        // 2. We reached the end of local data. Trigger Strava Sync for the preceding 30-day window.
        const lastActivity = activities[activities.length - 1];
        const oldestActivityDate = new Date(lastActivity.start_date);

        const syncEndDate = format(oldestActivityDate, 'yyyy-MM-dd');
        const syncStartDate = format(subDays(oldestActivityDate, 30), 'yyyy-MM-dd');

        // Trigger direct sync
        await apiClient.post(`sync/direct-sync?start_date=${syncStartDate}&end_date=${syncEndDate}`);

        // Invalidate tags to refresh metadata and total_pages
        dispatch(baseApi.util.invalidateTags(['Activity', 'ActivityStats', 'Analytics']));

        // Note: We don't increment page here. We wait for the invalidation to refresh the list.
        // If the sync added data, total_pages will increase, and the NEXT scroll will trigger a page load.
      }
    } catch (error) {
      console.error('Failed to fetch/sync more activities:', error);
    } finally {
      setIsSyncingMore(false);
    }
  }, [isSyncingMore, isFetching, isLoading, isOffline, activities, hasNoMoreInDb, activitiesData, page, dispatch]);

  // Ref to track if we've already triggered for the current visibility session
  const hasTriggeredRef = useRef(false);

  // Infinite scroll effect
  useEffect(() => {
    if (isLoading || isOffline) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver((entries) => {
      const entry = entries[0];

      if (entry.isIntersecting) {
        // Only trigger if we haven't triggered for this "bottom reaching" session
        // and we aren't currently busy syncing
        if (!hasTriggeredRef.current && !isSyncingMore && !isFetching && activities.length > 0) {
          fetchMoreActivities();
          hasTriggeredRef.current = true;
        }
      } else {
        // Reset the trigger when the sentinel leaves the viewport
        hasTriggeredRef.current = false;
      }
    }, {
      threshold: 0.1,
      rootMargin: '100px' // Start loading slightly before reaching the absolute bottom
    });

    if (sentinelRef.current) {
      observer.current.observe(sentinelRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [fetchMoreActivities, isLoading, isOffline, isSyncingMore, isFetching, activities.length]);

  // Handle view mode change
  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    nextView: ViewMode | null,
  ) => {
    if (nextView !== null) {
      setViewMode(nextView);
    }
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: ActivityFilterValues) => {
    setFilters(newFilters);
    setPage(1);
    setAccumulatedActivities([]); // Clear list on filter change
    setHasNoMoreInDb(false);
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      sportType: 'all',
      distanceRange: [0, 200],
      searchQuery: '',
      workoutId: 'all',
    });
    setPage(1);
  };



  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Offline Banner */}
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. Displaying cached activities data.
        </Alert>
      )}

      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Activities
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View and analyze your training activities
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="View mode"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <ViewModule />
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewList />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Activity Filters */}
      <ActivityFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onReset={handleResetFilters}
      />

      {/* Fetching Indicator */}
      {isFetching && !isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <Alert severity="info" icon={<CircularProgress size={20} sx={{ color: '#ff9800 !important' }} />}>
            Applying filters...
          </Alert>
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load activities.
          {error && 'status' in error ? ` (Status: ${error.status})` : ''}
          Please try again later.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={64} sx={{ color: '#ff9800 !important' }} />
        </Box>
      )}

      {/* Activities Display */}
      {!isLoading && !error && (
        <>
          {viewMode === 'grid' ? (
            <ActivityGridView activities={activities} navigate={navigate} />
          ) : (
            <ActivityListView activities={activities} navigate={navigate} />
          )}

          {/* Empty State */}
          {activities.length === 0 && !isLoading && !isFetching && activitiesData && activitiesData.activities.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No activities found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or sync your Strava account
              </Typography>
            </Box>
          )}

          {/* Initial processing state (ActivitiesData arrived but not accumulated yet) */}
          {activities.length === 0 && activitiesData && activitiesData.activities.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={64} sx={{ color: '#ff9800 !important' }} />
            </Box>
          )}

          {/* Bottom Sentinel for Infinite Scroll */}
          <Box
            ref={sentinelRef}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mt: 6,
              mb: 4,
              gap: 2,
              minHeight: '100px',
              justifyContent: 'center'
            }}
          >
            {isSyncingMore ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={40} sx={{ color: '#ff9800 !important' }} />
                <Typography variant="body2" color="text.secondary">
                  Syncing more activities...
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Alternatively, navigate to the <LinkIcon sx={{ fontSize: 16 }} />
                <Typography
                  component="span"
                  variant="body2"
                  color="primary"
                  sx={{ cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
                  onClick={() => navigate('/settings?sync=true')}
                >
                  Sync Settings
                </Typography>
                to manage your data.
              </Typography>
            )}
          </Box>

        </>
      )}
    </Container>
  );
};

export default ActivitiesListPage;